import { ChangeDetectionStrategy, Component, Inject, LOCALE_ID, OnDestroy, signal } from '@angular/core';
import { AsyncPipe, formatNumber, NgFor, NgIf } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { DateTime } from 'luxon';
import { SubSink } from 'subsink';
import { isEmpty, isNil, isNumber, toNumber } from 'lodash-es';
import { EmploymentAgreementInput } from '@startuptools/common/doc-gen';
import { DATE_FORMAT_LUXON, TIME_FORMAT_LUXON, TIME_FORMAT_LUXON_LOC } from '@startuptools/common/common';
import { DetailState, InputState } from '@startuptools/business-ui/identity/types';
import { JuridicalKind } from '../../../../models/identity.model';
import { DocumentDialogComponent } from '../../../../components/document-dialog/document-dialog.component';
import {
  GqlCompanyDirectorsService,
  GqlCreateIdentityInput,
  GqlCreateIdentityService,
  GqlDocGenScheduleDocGenService,
  GqlDocSystemTagKind,
} from '../../../../graphql/operations';
import {
  DocGenSchedReq,
  FOOTER_BILINGUAL_STARTUPTOOLS,
  FOOTER_ENG_STARTUPTOOLS,
} from '../../../../document-generation/types';
import { PartyObjectInputAdapter } from './party-object';
import { formatIdNumber } from '@startuptools/common/id-number';
import { InsuranceStore } from './employment-agreement-dialog.store';
import { PromptDialogComponent } from '../../../../components/simple-dialogs/prompt-dialog.component';
import { DocKind } from '../../../../free-templates/types';
import { defaultSignatory } from '../../../../helpers/default-signatory';
import { BigNumber } from 'bignumber.js';
import {
  AddIdentityDialogSettings,
  AddIdentityWrapperComponent,
} from '../../../../components/identity/add-identity-wrapper.component';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { GqlScriveAuthMethod, GqlDocumentSignatureMethod, GqlCompany } from '../../../../graphql/base-types.graphql';
import { QuillModule } from 'ngx-quill';
import { ObjectInputComponent } from '../../../../components/object-input/object-input.component';
import { SutStepComponent } from '../../../../modules/nav-stepper/step.component';
import { SutStepperComponent } from '../../../../modules/nav-stepper/stepper.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { LuxonModule } from 'luxon-angular';
import { IntegerOnlyDirective } from '../../../../directives/integer-only.directive';
import { MaxNumberValidatorDirective } from '../../../../directives/max-number.directive';
import { MinNumberValidatorDirective } from '../../../../directives/min-number.directive';
import { LocalizedNumericInputDirective } from '../../../../directives/number-input.directive';
import { toSignal } from '@angular/core/rxjs-interop';

enum EmploymentKind {
  Indefinite,
  Limited,
  Hourly,
}

enum Language {
  Swedish,
  English,
  Both,
}

export enum ScriveDeliveryMethod {
  Email = 'email',
  Mobile = 'mobile',
  EmailMobile = 'email_mobile',
  Pad = 'pad',
  API = 'api',
}

interface Data {
  docKind: DocKind.HourlyWage | DocKind.ManagingDirector | DocKind.PermanentEmployment;
  company: Pick<GqlCompany, 'id' | 'name' | 'addressAsStr' | 'organizationNumber'>;
}

const DEFAULT_WORK_HOURS = '40';
const DEFAULT_PROB_PERIOD = 6;
const DEFAULT_M_SALARY = new BigNumber(32000);

@Component({
  templateUrl: './employment-agreement-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true } }, InsuranceStore],
  imports: [
    SutStepComponent,
    SutStepperComponent,
    ObjectInputComponent,
    QuillModule,
    AddIdentityWrapperComponent,
    ReactiveFormsModule,
    FormsModule,
    MatSelect,
    MatOption,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatListModule,
    MatDatepickerModule,
    MatInputModule,
    MatCheckboxModule,
    MatSlider,
    MatSliderThumb,
    MatDialogClose,
    AsyncPipe,
    NgFor,
    LuxonModule,
    NgIf,
    IntegerOnlyDirective,
    MaxNumberValidatorDirective,
    MinNumberValidatorDirective,
    LocalizedNumericInputDirective,
  ],
})
export class EmploymentAgreementDialogComponent implements OnDestroy {
  DocKind = DocKind;
  EmploymentKind = EmploymentKind;
  Language = Language;

  private readonly DEFAULT_NOTICE_PERIOD = {
    [DocKind.ManagingDirector]: '6',
    [DocKind.PermanentEmployment]: '3',
    [DocKind.HourlyWage]: null as null,
  };

  formControls = {
    language: new FormControl<Language>(this.localeId === 'sv' ? Language.Swedish : Language.English),
    // @ts-expect-error TS2345
    employee: new FormControl<PartyObjectInputAdapter>(undefined),
    // @ts-expect-error TS2345
    employeeAddress: new FormControl<string>(undefined),
    kind: new FormControl<EmploymentKind>(EmploymentKind.Indefinite),
    probationary: new FormControl<boolean>(this.data.docKind !== DocKind.ManagingDirector),
    probationaryPeriod: new FormControl<number>(DEFAULT_PROB_PERIOD),
    hasNoticePeriod: new FormControl<boolean>(true),
    // @ts-expect-error TS2345
    noticePeriodEmployee: new FormControl<string>(this.DEFAULT_NOTICE_PERIOD[this.data.docKind]),
    // @ts-expect-error TS2345
    noticePeriodEmployer: new FormControl<string>(this.DEFAULT_NOTICE_PERIOD[this.data.docKind]),
    workHourStart: new FormControl<string>(DateTime.fromObject({ hour: 8 }).toFormat(TIME_FORMAT_LUXON)),
    workHourEnd: new FormControl<string>(DateTime.fromObject({ hour: 17 }).toFormat(TIME_FORMAT_LUXON)),
    // @ts-expect-error TS2345
    workingTimeNotSpecified: new FormControl<boolean>(undefined),
    position: new FormControl<string>(this.data.docKind === DocKind.ManagingDirector ? 'Verkställande direktör' : ''),
    positionSl: new FormControl<string>(this.data.docKind === DocKind.ManagingDirector ? 'Managing Director' : ''),
    // @ts-expect-error TS2345
    duties: new FormControl<string>(undefined),
    // @ts-expect-error TS2345
    dutiesSl: new FormControl<string>(undefined),
    hoursPerWeek: new FormControl<string>(DEFAULT_WORK_HOURS),
    partTime: new FormControl<boolean>(false),
    location: new FormControl<string>(''),
    startDate: new FormControl<DateTime>(DateTime.now().plus({ month: 1 }).startOf('month').startOf('day')),
    // @ts-expect-error TS2345
    endDate: new FormControl<DateTime>(undefined),
    vacationStartDate: new FormControl<number>(4),
    vacationDays: new FormControl<string>('25'),
    vacationKind: new FormControl<0 | 1>(0),
    vacationAdvance: new FormControl<number>(0),
    vacationClawBack: new FormControl<boolean>(true),
    salary: new FormControl<BigNumber>(DEFAULT_M_SALARY),
    //benefits
    pension: new FormControl<boolean>(false),
    preventiveCare: new FormControl<boolean>(false),
    occupationalInjuryInsurance: new FormControl<boolean>(false),
    serviceGroupLifeInsurance: new FormControl<boolean>(false),
    healthInsurance: new FormControl<boolean>(false),
    healthCareInsurance: new FormControl<boolean>(false),
    mobilePhonePlan: new FormControl<boolean>(false),
    penalty: new FormControl<string>('50000'),
    nonSolicitationPeriod: new FormControl<number>(12),
    companySignatory: new FormControl<PartyObjectInputAdapter | null>(null),
    companyAddress: new FormControl<string | null>(null),
  };

  formGroups = {
    employee: new FormGroup({
      employee: this.formControls.employee,
      address: this.formControls.employeeAddress,
    }),
    company: new FormGroup({
      address: this.formControls.companyAddress,
    }),
    language: new FormGroup({
      language: this.formControls.language,
    }),
    employment: new FormGroup({
      kind: this.formControls.kind,
      probationary: this.formControls.probationary,
      probationaryPeriod: this.formControls.probationaryPeriod,
      workingTimeNotSpecified: this.formControls.workingTimeNotSpecified,
      hoursPerWeek: this.formControls.hoursPerWeek,
      partTime: this.formControls.partTime,
      startDate: this.formControls.startDate,
      endDate: this.formControls.endDate,
      hasNoticePeriod: this.formControls.hasNoticePeriod,
      noticePeriodEmployee: this.formControls.noticePeriodEmployee,
      noticePeriodEmployer: this.formControls.noticePeriodEmployer,
      workHourStart: this.formControls.workHourStart,
      workHourEnd: this.formControls.workHourEnd,
    }),
    role: new FormGroup({
      position: this.formControls.position,
      positionSl: this.formControls.positionSl,
      duties: this.formControls.duties,
      dutiesSl: this.formControls.dutiesSl,
      location: this.formControls.location,
    }),
    vacation: new FormGroup({
      startDate: this.formControls.vacationStartDate,
      vacationDays: this.formControls.vacationDays,
      vacationKind: this.formControls.vacationKind,
      vacationAdvance: this.formControls.vacationAdvance,
      vacationClawBack: this.formControls.vacationClawBack,
    }),
    salary: new FormGroup({
      salary: this.formControls.salary,
    }),
    benefits: new FormGroup({
      pension: this.formControls.pension,
      preventiveCare: this.formControls.preventiveCare,
      occupationalInjuryInsurance: this.formControls.occupationalInjuryInsurance,
      serviceGroupLifeInsurance: this.formControls.serviceGroupLifeInsurance,
      healthInsurance: this.formControls.healthInsurance,
      healthCareInsurance: this.formControls.healthCareInsurance,
      mobilePhonePlan: this.formControls.mobilePhonePlan,
    }),
    confidentiality: new FormGroup({
      penalty: this.formControls.penalty,
      nonSolicitationPeriod: this.formControls.nonSolicitationPeriod,
    }),
    signatory: new FormGroup({
      companySignatory: this.formControls.companySignatory,
    }),
  };
  form = new FormGroup(this.formGroups);

  subs = new SubSink();

  languageSub = new BehaviorSubject<Language>(Language.Swedish);
  language$ = this.languageSub.asObservable();

  company$ = of(this.data.company);
  companyId = toSignal(this.company$.pipe(map(c => c.id)));

  vacationRange$ = of(null).pipe(
    map(() => {
      const start1 = DateTime.fromObject({ year: 2000, month: 1 });
      const end1 = start1.plus({ month: 11 }).endOf('month');
      const start2 = DateTime.fromObject({ year: 2000, month: 4 });
      const end2 = start2.plus({ month: 11 }).endOf('month');
      return [
        { i: 1, start: start1, end: end1 },
        { i: 4, start: start2, end: end2 },
      ];
    }),
  );

  insurances$ = this.insuranceStore.state$.pipe(map(s => s.insurances));

  maxNoticePeriodSub = new BehaviorSubject<{
    kind: EmploymentKind;
    startDate: DateTime;
    endDate: DateTime;
    // @ts-expect-error TS2345
  }>(null);
  maxNoticePeriod$ = this.maxNoticePeriodSub.asObservable().pipe(
    map(({ kind, startDate, endDate }) => {
      if (kind !== EmploymentKind.Limited) {
        return DEFAULT_PROB_PERIOD;
      }
      if (isNil(endDate)) {
        return 3;
      }
      const duration = endDate?.plus({ day: 1 }).diff(startDate, 'months', { conversionAccuracy: 'longterm' }).months;
      if (duration < 3) {
        if (toNumber(this.formControls.noticePeriodEmployee.value) > 1) {
          this.formControls.noticePeriodEmployee.setValue('1');
        }
        if (toNumber(this.formControls.noticePeriodEmployer.value) > 1) {
          this.formControls.noticePeriodEmployer.setValue('1');
        }
        return 1;
      }
      if (toNumber(this.formControls.noticePeriodEmployee.value) > 3) {
        this.formControls.noticePeriodEmployee.setValue('3');
      }
      if (toNumber(this.formControls.noticePeriodEmployer.value) > 3) {
        this.formControls.noticePeriodEmployer.setValue('3');
      }
      return 3;
    }),
    shareReplay(0),
  );

  benefitsHasErrors$ = combineLatest([this.insurances$, this.language$]).pipe(
    map(([insuarances, language]) => {
      if (language === Language.Swedish) {
        return insuarances.some(m => isEmpty(m.primary_lang));
      } else if (language === Language.English) {
        return insuarances.some(m => isEmpty(m.secondary_lang));
      } else if (language === Language.Both) {
        return insuarances.some(m => isEmpty(m.primary_lang) || isEmpty(m.secondary_lang));
      }
    }),
  );

  public open = signal(false);
  public settings = signal<AddIdentityDialogSettings | null>(null);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Data,
    @Inject(LOCALE_ID) private localeId: string,
    public dialogRef: MatDialogRef<EmploymentAgreementDialogComponent>,
    private dialog: MatDialog,
    private gqlCreateIdentity: GqlCreateIdentityService,
    private gqlScheduleDocGen: GqlDocGenScheduleDocGenService,
    private gqlCompanyDirectors: GqlCompanyDirectorsService,
    private insuranceStore: InsuranceStore,
  ) {
    this.subs.sink = this.language$.subscribe(l => {
      if (l === Language.English) {
        this.formControls.position.setValidators([]);
        this.formControls.position.updateValueAndValidity();
      } else if (l === Language.Swedish) {
        this.formControls.positionSl.setValidators([]);
        this.formControls.positionSl.updateValueAndValidity();
      }
    });
    // @ts-expect-error TS2345
    this.subs.sink = this.formControls.language.valueChanges.subscribe(l => this.languageSub.next(l));

    this.subs.sink = this.formControls.kind.valueChanges.subscribe(kind => {
      switch (kind) {
        case EmploymentKind.Hourly: {
          this.formControls.probationary.setValue(false);
          this.formControls.probationary.updateValueAndValidity();
          this.formControls.probationaryPeriod.setValue(DEFAULT_PROB_PERIOD);
          this.formControls.probationaryPeriod.updateValueAndValidity();
          this.formControls.hoursPerWeek.setValue(DEFAULT_WORK_HOURS);
          this.formControls.hoursPerWeek.updateValueAndValidity();
          this.formControls.salary.setValue(new BigNumber(200));
          this.formControls.salary.updateValueAndValidity();
          this.formControls.hasNoticePeriod.setValue(false);
          this.formControls.hasNoticePeriod.updateValueAndValidity();
          break;
        }
        case EmploymentKind.Indefinite: {
          this.formControls.probationary.setValue(true);
          this.formControls.probationary.updateValueAndValidity();
          this.formControls.probationaryPeriod.setValue(DEFAULT_PROB_PERIOD);
          this.formControls.probationaryPeriod.updateValueAndValidity();
          this.formControls.hoursPerWeek.setValue(DEFAULT_WORK_HOURS);
          this.formControls.hoursPerWeek.updateValueAndValidity();
          this.formControls.salary.setValue(DEFAULT_M_SALARY);
          this.formControls.salary.updateValueAndValidity();
          this.formControls.hasNoticePeriod.setValue(true);
          this.formControls.hasNoticePeriod.updateValueAndValidity();
          break;
        }
        case EmploymentKind.Limited: {
          this.formControls.probationary.setValue(false);
          this.formControls.probationary.updateValueAndValidity();
          this.formControls.probationaryPeriod.setValue(DEFAULT_PROB_PERIOD);
          this.formControls.probationaryPeriod.updateValueAndValidity();
          this.formControls.salary.setValue(DEFAULT_M_SALARY);
          this.formControls.salary.updateValueAndValidity();
          this.formControls.hasNoticePeriod.setValue(false);
          this.formControls.hasNoticePeriod.updateValueAndValidity();
          this.formControls.startDate.updateValueAndValidity();
          this.formControls.endDate.updateValueAndValidity();
          break;
        }
        case null: {
          return;
        }
      }
    });
    this.subs.sink = this.formControls.partTime.valueChanges.subscribe(partTime => {
      if (!partTime) {
        this.formControls.hoursPerWeek.setValue(DEFAULT_WORK_HOURS);
        this.formControls.hoursPerWeek.updateValueAndValidity();
      }
    });
    this.subs.sink = this.formControls.workingTimeNotSpecified.valueChanges.subscribe(wt => {
      if (wt) {
        this.formControls.hoursPerWeek.setValue(DEFAULT_WORK_HOURS);
        this.formControls.hoursPerWeek.updateValueAndValidity();
      }
    });
    this.subs.sink = this.company$
      .pipe(
        first(),
        switchMap(company => this.gqlCompanyDirectors.fetch({ companyId: company.id })),
        map(res => res.data.companyDirectors),
      )
      .subscribe(cds => {
        const cd = defaultSignatory(cds, data.docKind === DocKind.ManagingDirector);
        if (!isNil(cd)) {
          const identity = cd.identity;
          this.formControls.companySignatory.setValue(
            new PartyObjectInputAdapter({
              model: identity,
              name: identity.name,
              sub: identity.identityNumber,
            }),
          );
        }
      });
    this.subs.sink = this.formControls.kind.valueChanges.subscribe(kind => {
      if (kind === EmploymentKind.Limited) {
        this.formControls.endDate.setValidators([Validators.required]);
      } else {
        setTimeout(() => {
          this.formControls.endDate.removeValidators([Validators.required]);
          this.formControls.endDate.updateValueAndValidity();
          this.formControls.endDate.reset();
        });
      }
    });
    this.subs.sink = this.formControls.nonSolicitationPeriod.valueChanges.subscribe(value => {
      this.formControls.nonSolicitationPeriod.setValue(value, {
        emitEvent: false,
      });
    });
    this.subs.sink = this.formControls.noticePeriodEmployee.valueChanges.subscribe(value => {
      this.formControls.noticePeriodEmployee.setValue(value, {
        emitEvent: false,
      });
    });
    this.subs.sink = this.formControls.noticePeriodEmployer.valueChanges.subscribe(value => {
      this.formControls.noticePeriodEmployer.setValue(value, {
        emitEvent: false,
      });
    });
    this.subs.sink = this.formControls.probationaryPeriod.valueChanges.subscribe(value => {
      this.formControls.probationaryPeriod.setValue(value, {
        emitEvent: false,
      });
    });
    this.subs.sink = combineLatest({
      kind: this.formControls.kind.valueChanges.pipe(
        // @ts-expect-error TS2345
        startWith(this.formControls.kind.value),
      ) as Observable<EmploymentKind>,
      startDate: this.formControls.startDate.valueChanges.pipe(
        // @ts-expect-error TS2345
        startWith(this.formControls.startDate.value),
      ) as Observable<DateTime>,
      endDate: this.formControls.endDate.valueChanges.pipe(
        startWith(this.formControls.endDate.value),
      ) as Observable<DateTime>,
    }).subscribe(res => {
      this.maxNoticePeriodSub.next(res);
    });

    this.subs.sink = this.formControls.employee.valueChanges.subscribe((employee: PartyObjectInputAdapter) => {
      // @ts-expect-error TS2345
      this.formControls.employeeAddress.setValue(employee?.party?.model?.address?.replaceAll('\n', ', '));
    });

    // this.company$.pipe(first()).subscribe(company => {
    //   this.formControls.companyAddress.setValue(company.addressAsStr);
    // });

    this.formControls.kind.setValue(EmploymentKind.Indefinite);
  }

  static open(dialog: MatDialog, data: Data): MatDialogRef<EmploymentAgreementDialogComponent> {
    return dialog.open<EmploymentAgreementDialogComponent, Data>(EmploymentAgreementDialogComponent, {
      width: '1200px',
      maxHeight: '100%',
      disableClose: true,
      closeOnNavigation: true,
      data,
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private selectParty(
    headline: string,
    saveFunction: (identity: GqlCreateIdentityInput) => Observable<GqlCreateIdentityInput | undefined>,
  ) {
    this.settings.set({
      headline,
      availableInputStates: {
        [InputState.CompanyBoard]: false,
        [InputState.System]: true,
        [InputState.Swedish]: true,
        [InputState.Foreign]: true,
      },
      detailFieldsSettings: {
        address: DetailState.Required,
        countryCode: DetailState.Required,
        email: DetailState.Required,
      },
      handleCreate: identity => {
        return firstValueFrom(saveFunction(identityToInput(identity)).pipe(tap(() => this.open.set(false))));
      },
      juridicalKinds: [JuridicalKind.Person],
    });
    this.open.set(true);
  }

  selectEmployee() {
    this.selectParty($localize`Person som ska anställas`, identity => {
      return this.company$.pipe(
        first(),
        concatMap(company => {
          const input = identityToInput(identity);
          return this.gqlCreateIdentity.mutate({ companyId: company.id, input }).pipe(
            map(res => res.data?.createIdentity),
            tap(identity => {
              if (identity) {
                this.formControls.employee.setValue(
                  new PartyObjectInputAdapter({
                    model: identity,
                    name: identity.name,
                    sub: identity.identityNumber,
                  }),
                );
              }
            }),
          );
        }),
      );
    });
  }

  private selectSignatory(
    headline: string,
    saveFunction: (identity: GqlCreateIdentityInput) => Observable<GqlCreateIdentityInput | undefined>,
  ) {
    this.settings.set({
      headline,
      availableInputStates: {
        [InputState.CompanyBoard]: false,
        [InputState.System]: true,
        [InputState.Swedish]: true,
        [InputState.Foreign]: true,
      },
      detailFieldsSettings: {
        address: DetailState.Required,
        countryCode: DetailState.Required,
        email: DetailState.Required,
      },
      relations: {
        companyDirector: true,
      },
      handleCreate: identity => {
        return firstValueFrom(saveFunction(identityToInput(identity)).pipe(tap(() => this.open.set(false))));
      },
      juridicalKinds: [JuridicalKind.Person],
    });
    this.open.set(true);
  }

  selectSignatoryOne() {
    this.selectSignatory($localize`Vem skriver under för företaget`, identity => {
      return this.company$.pipe(
        first(),
        concatMap(company => {
          const input = identityToInput(identity);
          return this.gqlCreateIdentity.mutate({ companyId: company.id, input }).pipe(
            map(res => res.data?.createIdentity),
            tap(identity => {
              if (identity) {
                this.formControls.companySignatory.setValue(
                  new PartyObjectInputAdapter({
                    model: identity,
                    name: identity.name,
                    sub: identity.identityNumber,
                  }),
                );
              }
            }),
          );
        }),
      );
    });
  }

  addInsurance() {
    PromptDialogComponent.open(
      this.dialog,
      {
        title: $localize`Lägg till förmån`,
        validators: [Validators.required],
        errors: { required: $localize`Måste fyllas i` },
        type: 'text',
        modelValue: null,
        modelValueSl: null,
        cancel: $localize`Avbryt`,
        ok: $localize`LÄGG TILL`,
        placeholder: $localize`Beskrivning av förmån`,
        placeholderSl: $localize`Beskrivning av förmån (engelska)`,
        // @ts-expect-error TS2345
        primaryLang: [Language.Swedish, Language.Both].includes(this.formControls.language.value),
        // @ts-expect-error TS2345
        secondaryLang: [Language.English, Language.Both].includes(this.formControls.language.value),
        save: ({ input, inputSl }) => {
          this.insuranceStore.addInsurance({
            // @ts-expect-error TS2322
            primary_lang: input,
            // @ts-expect-error TS2322
            secondary_lang: inputSl,
          });
          return of(null);
        },
      },
      { minWidth: 500 },
    );
  }

  editInsurance(idx: number) {
    this.insurances$.pipe(first()).subscribe(insurances => {
      const insurance = insurances[idx];
      PromptDialogComponent.open(
        this.dialog,
        {
          title: $localize`Editera förmån`,
          validators: [Validators.required],
          errors: { required: $localize`Måste fyllas i` },
          type: 'text',
          // @ts-expect-error TS18048
          modelValue: insurance.primary_lang,
          // @ts-expect-error TS18048
          modelValueSl: insurance.secondary_lang,
          cancel: $localize`Avbryt`,
          ok: $localize`Spara`,
          placeholder: $localize`Beskrivning av förmån`,
          placeholderSl: $localize`Beskrivning av förmån (engelska)`,
          // @ts-expect-error TS2345
          primaryLang: [Language.Swedish, Language.Both].includes(this.formControls.language.value),
          // @ts-expect-error TS2345
          secondaryLang: [Language.English, Language.Both].includes(this.formControls.language.value),
          save: ({ input, inputSl }) => {
            this.insuranceStore.updateInsurance(idx, {
              // @ts-expect-error TS2322
              primary_lang: input,
              // @ts-expect-error TS2322
              secondary_lang: inputSl,
            });
            return of(null);
          },
        },
        { minWidth: 500 },
      );
    });
  }

  rmInsurance(idx: number) {
    this.insuranceStore.rmInsurance(idx);
  }

  submit() {
    if (this.form.invalid) {
      return;
    }
    const formData = this.form.value;
    return combineLatest([this.company$, this.insurances$])
      .pipe(
        first(),
        concatMap(([company, insurances]) => {
          const filename = `Anställningsavtal - ${company.name} - ${formData.employee!.employee!.name}.pdf`;
          const companySignIdentity = formData.signatory!.companySignatory!.party.model;
          const employeeIdentity = formData.employee!.employee!.party.model;
          const vacationStart = DateTime.fromObject({
            year: 2000,
            month: formData.vacation!.startDate as number,
          })
            .startOf('month')
            .toUTC(0, { keepLocalTime: true });
          const vacationEnd = vacationStart.plus({ months: 11 }).endOf('month').toUTC(0, { keepLocalTime: true });
          return this.gqlScheduleDocGen
            .mutate({
              companyId: company.id,
              input: {
                genSchedRequest: {
                  companyId: company.id,
                  filename,
                  lockdown: false,
                  kind: GqlDocSystemTagKind.EmploymentAgreement,
                  userTags: ['Anställningsavtal'],
                  signature: {
                    method: GqlDocumentSignatureMethod.Digital,
                    parties: [
                      {
                        auth_method_to_sign: companySignIdentity.isSwedish
                          ? GqlScriveAuthMethod.SeBankid
                          : GqlScriveAuthMethod.Standard,
                        auth_method_to_view: GqlScriveAuthMethod.Standard,
                        delivery_method: ScriveDeliveryMethod.Email,
                        email: companySignIdentity.email!,
                        signatory_id: formData.signatory!.companySignatory!.identityId,
                        proxy_id: null,
                      },
                      {
                        auth_method_to_sign: employeeIdentity.isSwedish
                          ? GqlScriveAuthMethod.SeBankid
                          : GqlScriveAuthMethod.Standard,
                        auth_method_to_view: GqlScriveAuthMethod.Standard,
                        delivery_method: ScriveDeliveryMethod.Email,
                        email: employeeIdentity.email!,
                        signatory_id: formData.employee!.employee!.identityId,
                        proxy_id: null,
                      },
                    ],
                  },
                  docGens: [
                    {
                      docGenDetails: {
                        templateId: 'agreements/employment_agreement.html.j2',
                        footerPath:
                          formData.language!.language === Language.Both
                            ? FOOTER_BILINGUAL_STARTUPTOOLS
                            : FOOTER_ENG_STARTUPTOOLS,
                        filename,
                        // @ts-expect-error TS2322
                        inputData: {
                          company_name: company.name,
                          organization_number: formatIdNumber(company.organizationNumber)!,
                          company_address: formData.company!.address!,
                          is_managing_director: this.data.docKind === DocKind.ManagingDirector,
                          primary_lang: [Language.Swedish, Language.Both].includes(formData.language!.language!),
                          secondary_lang: [Language.English, Language.Both].includes(formData.language!.language!),
                          employee: {
                            name: employeeIdentity.name!,
                            identity_number: employeeIdentity.isSwedish
                              ? formatIdNumber(employeeIdentity.identityNumber)!
                              : '',
                            email: employeeIdentity.email,
                            address: formData.employee!.address!,
                          },
                          employment: {
                            kind: formData.employment!.kind!,
                            probationary: formData.employment!.probationary!,
                            probationary_period: formData.employment!.probationaryPeriod!,
                            part_time: formData.employment!.partTime!,
                            hours_per_week: toNumber(formData.employment!.hoursPerWeek),
                            start_date: {
                              primary_lang: formData.employment!.startDate!.toFormat(DATE_FORMAT_LUXON)!,
                              secondary_lang: formData.employment!.startDate!.toFormat(DATE_FORMAT_LUXON)!,
                            },
                            end_date: !isNil(formData.employment!.endDate)
                              ? {
                                  primary_lang: formData.employment!.endDate?.toFormat(DATE_FORMAT_LUXON),
                                  secondary_lang: formData.employment!.endDate?.toFormat(DATE_FORMAT_LUXON),
                                }
                              : undefined,
                            hours_not_specified: formData.employment!.workingTimeNotSpecified,
                            has_notice_period: formData.employment!.hasNoticePeriod!,
                            notice_period_employee: toNumber(formData.employment!.noticePeriodEmployee),
                            notice_period_employer: toNumber(formData.employment!.noticePeriodEmployer),
                            side_hustle_banned: false,
                            starting_hour: {
                              primary_lang: formData.employment!.workHourStart!,
                              secondary_lang: DateTime.fromFormat(
                                formData.employment!.workHourStart!,
                                TIME_FORMAT_LUXON,
                              ).toFormat(TIME_FORMAT_LUXON_LOC, {
                                locale: 'en',
                              }),
                            },
                            ending_hour: {
                              primary_lang: formData.employment!.workHourEnd!,
                              secondary_lang: DateTime.fromFormat(
                                formData.employment!.workHourEnd!,
                                TIME_FORMAT_LUXON,
                              ).toFormat(TIME_FORMAT_LUXON_LOC, {
                                locale: 'en',
                              }),
                            },
                          },
                          role: {
                            duties: {
                              // @ts-expect-error TS2322
                              primary_lang: !isEmpty(formData.role.duties)
                                ? // @ts-expect-error TS18048
                                  `${formData.role.duties.trim()}.`.replace('..', '.')
                                : undefined,
                              // @ts-expect-error TS2322
                              secondary_lang: !isEmpty(formData.role.dutiesSl)
                                ? // @ts-expect-error TS18048
                                  `${formData.role.dutiesSl.trim()}.`.replace('..', '.')
                                : undefined,
                            },
                            position: {
                              // @ts-expect-error TS18048
                              primary_lang: !isEmpty(formData.role.position) ? `${formData.role.position.trim()}` : '',
                              // @ts-expect-error TS18048
                              secondary_lang: !isEmpty(formData.role.positionSl)
                                ? // @ts-expect-error TS18048
                                  `${formData.role.positionSl.trim()}`
                                : '',
                            },
                            // @ts-expect-error TS2322
                            location: formData.role.location,
                          },
                          commitment: {
                            non_solicitation_period: formData.confidentiality!.nonSolicitationPeriod!,
                            penalty_for_confidentiality_breach: {
                              // @ts-expect-error TS18048
                              primary_lang: formatNumber(toNumber(formData.confidentiality.penalty), 'sv'),
                              // @ts-expect-error TS18048
                              secondary_lang: formatNumber(toNumber(formData.confidentiality.penalty), 'en'),
                            },
                          },
                          salary: {
                            // @ts-expect-error TS18048
                            primary_lang: formatNumber(formData.salary.salary.toNumber(), 'sv'),
                            // @ts-expect-error TS18048
                            secondary_lang: formatNumber(formData.salary.salary.toNumber(), 'en'),
                          },
                          vacation: {
                            // @ts-expect-error TS18048
                            days: toNumber(formData.vacation.vacationDays),
                            // @ts-expect-error TS18048
                            advance_vacation_days: isNumber(formData.vacation.vacationAdvance)
                              ? // @ts-expect-error TS18048
                                formData.vacation.vacationAdvance
                              : // @ts-expect-error TS18048
                                toNumber(formData.vacation.vacationDays),
                            // @ts-expect-error TS2322
                            year: formData.vacation.vacationKind,
                            year_start: {
                              primary_lang: vacationStart.setLocale('sv').toFormat('d MMMM'),
                              secondary_lang: vacationStart.setLocale('en').toFormat('d MMMM'),
                            },
                            year_end: {
                              primary_lang: vacationEnd.setLocale('sv').toFormat('d MMMM'),
                              secondary_lang: vacationEnd.setLocale('en').toFormat('d MMMM'),
                            },
                            // @ts-expect-error TS2322
                            claw_back_of_advance_vacation: formData.vacation.vacationClawBack,
                          },
                          dispute_resolution: {
                            primary_lang_prevails: true,
                          },
                          benefits: {
                            // @ts-expect-error TS2322
                            pension: formData.benefits.pension,
                            // @ts-expect-error TS2322
                            preventive_care: formData.benefits.preventiveCare,
                            insurances: [
                              // @ts-expect-error TS18048
                              ...(formData.benefits.occupationalInjuryInsurance
                                ? [
                                    {
                                      primary_lang: 'Trygghetsförsäkring vid arbetsskada (TFA)',
                                      secondary_lang: 'Occupational injury insurance (TFA)',
                                    },
                                  ]
                                : []),
                              // @ts-expect-error TS18048
                              ...(formData.benefits.serviceGroupLifeInsurance
                                ? [
                                    {
                                      primary_lang: 'Tjänstegrupplivsförsäkring (TGL)',
                                      secondary_lang: 'Service Group Life Insurance (TGL)',
                                    },
                                  ]
                                : []),
                              // @ts-expect-error TS18048
                              ...(formData.benefits.healthInsurance
                                ? [
                                    {
                                      primary_lang: 'Sjukförsäkring',
                                      secondary_lang: 'Health insurance',
                                    },
                                  ]
                                : []),
                              // @ts-expect-error TS18048
                              ...(formData.benefits.healthCareInsurance
                                ? [
                                    {
                                      primary_lang: 'Sjukvårdsförsäkring',
                                      secondary_lang: 'Health care insurance',
                                    },
                                  ]
                                : []),
                              // @ts-expect-error TS18048
                              ...(formData.benefits.mobilePhonePlan
                                ? [
                                    {
                                      primary_lang: 'Mobiltelefonabonnemang',
                                      secondary_lang: 'Mobile phone plan',
                                    },
                                  ]
                                : []),
                              ...insurances,
                            ],
                          },
                          digital_signature: true,
                          has_appendix: false,
                          signatures: [],
                        } satisfies EmploymentAgreementInput,
                        signatureParties: [],
                      },
                    },
                  ],
                } satisfies DocGenSchedReq<EmploymentAgreementInput>,
              },
            })
            .pipe(map(res => res.data!.docGenScheduleDocGen));
        }),
      )
      .subscribe(res => {
        this.dialogRef.close();
        DocumentDialogComponent.open(this.dialog, { documentId: res.id, companyId: res.companyId });
      });
  }
}
